// theme-sources/js/theme/components/cart-upsell.js

import { publish, THEME_EVENTS } from "../../shared/events";
import { fetchConfig } from "../core/utils";
import { syncCartFakeItem } from "../features/cart-fake-item";
import { logger } from "../core/logger";

class CartUpsell extends HTMLElement {
  connectedCallback() {
    this.checkbox = this.querySelector(".free_item");
    this.cartDrawer = document.querySelector("cart-drawer");
    this.ziggyDrawer = document.querySelector("ziggy-drawer[name='cart']");

    if (!this.checkbox) {
      logger.warn("CartUpsell", "connectedCallback: checkbox not found");
      return;
    }

    if (!this.cartDrawer) {
      logger.warn("CartUpsell", "connectedCallback: cartDrawer not found");
      return;
    }

    this.onChange = this.onChange.bind(this);
    this.checkbox.addEventListener("change", this.onChange);

    logger.info("CartUpsell", "connected");
  }

  disconnectedCallback() {
    this.checkbox?.removeEventListener("change", this.onChange);
    logger.info("CartUpsell", "disconnected");
  }

  async onChange(event) {
    const checkbox = event.target;
    const variantId = Number(checkbox.value);

    logger.info("CartUpsell", "change", {
      checked: checkbox.checked,
      variantId,
    });

    if (!variantId) {
      logger.warn("CartUpsell", "change: invalid variantId");
      return;
    }

    checkbox.disabled = true;

    try {
      const sections = this.cartDrawer
        .getSectionsToRender()
        .map((section) => section.id);

      let baseState = null;

      if (checkbox.checked) {
        logger.info("CartUpsell", "adding upsell item", {
          variantId,
          sections,
        });

        const response = await fetch(`${theme.routes.cart_add_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections,
            sections_url: window.location.pathname,
          }),
        });

        baseState = await response.json();

        if (!response.ok) {
          throw new Error(
            `Upsell add failed (${response.status}) ${baseState?.description || baseState?.message || ""}`,
          );
        }
      } else {
        logger.info("CartUpsell", "removing upsell item", {
          variantId,
          sections,
        });

        const cartState = await fetch(`${theme.routes.cart_url}`, {
          headers: { Accept: "application/json" },
        }).then((response) => response.json());

        const item = (cartState.items || []).find(
          (cartItem) => Number(cartItem.id) === variantId,
        );

        if (item) {
          const response = await fetch(`${theme.routes.cart_update_url}`, {
            ...fetchConfig(),
            body: JSON.stringify({
              updates: { [item.key]: 0 },
              sections,
              sections_url: window.location.pathname,
            }),
          });

          baseState = await response.json();

          if (!response.ok) {
            throw new Error(
              `Upsell remove failed (${response.status}) ${baseState?.description || baseState?.message || ""}`,
            );
          }
        } else {
          logger.warn("CartUpsell", "remove: item not found in cart", {
            variantId,
          });
          baseState = cartState;
        }
      }

      logger.info("CartUpsell", "syncing fake item", {
        sections,
        hasBaseState: !!baseState,
      });

      const finalState = await syncCartFakeItem({
        baseState,
        sections,
        sectionsUrl: window.location.pathname,
      });

      const drawerSectionId = this.cartDrawer.dataset.sectionId;
      const hasRenderableSections =
        !!finalState?.sections &&
        !!drawerSectionId &&
        !!finalState.sections[drawerSectionId] &&
        !!finalState.sections["cart-icon-bubble"];

      logger.info("CartUpsell", "sync complete", {
        itemCount: finalState?.item_count,
        hasSections: !!finalState?.sections,
        returnedSections: Object.keys(finalState?.sections || {}),
        hasRenderableSections,
      });

      publish(THEME_EVENTS.cartUpdate, {
        source: "cart-upsell",
        productVariantId: variantId,
        cartData: finalState,
      });

      if (hasRenderableSections) {
        this.cartDrawer.renderContents(finalState);
      }

      this.ziggyDrawer?.open();
    } catch (error) {
      logger.error("CartUpsell", "onChange error", error);
      checkbox.checked = !checkbox.checked;
    } finally {
      checkbox.disabled = false;
    }
  }
}

if (!customElements.get("cart-upsell")) {
  customElements.define("cart-upsell", CartUpsell);
}
